// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — how the research tools name a frame's time: as the date track
 * does (`frameCalendar.ts`), at the axis's precision ("1991", "1991-01"),
 * never a playhead instant such as "1991-01-02 08:37" on annual data.
 *
 * The host says which axis the dataset on the main globe has (a stream's
 * `.dsa` frames); without one (a catalogue dataset, a still picture) the
 * instant is kept as it was. Times that come from a straight line through
 * the axis (a graph's slot, a diagram's row) are first put on the frame
 * nearest them, the frame they stand for.
 */
import {
  formatFrameIso,
  formatFrameLabel,
  frameTimeMs,
  nearestFrameAtTimeMs,
  precisionForCadence,
  type FrameAxis,
  type FramePrecision,
} from '../frameCalendar'

let source: () => FrameAxis | null = () => null

/** The host's axis of the dataset on the main globe (null when it has none). */
export function setResearchFrameAxis(fn: () => FrameAxis | null): void {
  source = fn
}

export function researchFrameAxis(): FrameAxis | null {
  try {
    return source()
  } catch {
    return null
  }
}

/** The precision of the main globe's axis; null without one. */
export function researchPrecision(axis: FrameAxis | null = researchFrameAxis()): FramePrecision | null {
  return axis?.precision ?? null
}

/** A time as the stamp of the frame nearest it; unchanged without an axis. */
export function snapToFrame(ms: number, axis: FrameAxis | null = researchFrameAxis()): number {
  if (!axis || !Number.isFinite(ms)) return ms
  return frameTimeMs(axis, nearestFrameAtTimeMs(axis, ms))
}

/** A time for a file: the frame's date at the axis's precision; the full ISO instant without an axis. */
export function frameIsoForFile(ms: number, axis: FrameAxis | null = researchFrameAxis()): string {
  if (!axis) return new Date(ms).toISOString()
  return formatFrameIso(snapToFrame(ms, axis), axis.precision)
}

/** A time for a label on screen: the frame's own label ("Jan 1991"); null without an axis. */
export function frameLabelNow(ms: number, axis: FrameAxis | null = researchFrameAxis()): string | null {
  if (!axis) return null
  return formatFrameLabel(snapToFrame(ms, axis), axis.precision)
}

/** The precision a frame of this cadence is read at (another globe's or an overlay's frame). */
export function precisionOfCadence(cadenceMs: number): FramePrecision {
  return precisionForCadence(cadenceMs)
}
